import React, { type ReactNode } from 'react';
import { cn } from '../../../utils';
import type { Position } from '../../../types';
import { XIcon } from '../../shared/icons';

export interface MinimalWidgetPanelProps {
  /** Whether panel is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Position of the panel */
  position?: Position;
  /** Children content */
  children: ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * Minimal style widget panel - Minimal-style with compact, sharp design
 */
export function MinimalWidgetPanel({
  isOpen,
  onClose,
  position = 'bottom-left',
  children,
  className,
}: MinimalWidgetPanelProps) {
  if (!isOpen) return null;

  const panelWidth = 340;

  const positionStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: 24,
    ...(position === 'bottom-left' ? { left: 24 } : { right: 24 }),
    width: Math.min(panelWidth, typeof window !== 'undefined' ? window.innerWidth - 48 : panelWidth),
    height: typeof window !== 'undefined' ? Math.min(window.innerHeight - 80, 560) : 440,
    zIndex: 'var(--qcall-z-panel)',
    borderRadius: 16,
  };

  return (
    <div
      className={cn(
        'flex flex-col',
        'bg-[#3a3b3c]',
        'shadow-[0_12px_40px_rgb(0,0,0,0.5)]',
        'border border-[#4f4f4f]',
        'animate-in slide-in-from-bottom-4 zoom-in-95 fade-in duration-200',
        'overflow-hidden',
        className
      )}
      style={positionStyles}
      role="dialog"
      aria-modal="true"
      aria-label="Voice call panel"
    >
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={onClose}
          className="flex items-center justify-center w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close panel"
        >
          <XIcon className="w-3.5 h-3.5 text-white/70" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col pt-12 pb-6 relative">
        {children}
      </div>

      {/* Footer */}
      <p className="text-white/40 text-[9px] uppercase tracking-[0.2em] font-bold text-center py-2">Powered by QCALL AI</p>
    </div>
  );
}
